// Betting Leaderboard view — extracted from Betting.js.
// Shows player P&L, wins, holes won, birdies/pars/eagles, avg score, best round,
// and an estimated WHS handicap calculated from completed rounds.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trophy } from 'lucide-react';

import { API } from './constants';

const HCP_MARK = { calculated: '*', entered: '**' };
const formatMoney = (v) => {
  if (v > 0) return `+$${v.toFixed(0)}`;
  if (v < 0) return `-$${Math.abs(v).toFixed(0)}`;
  return '$0';
};
const RANK_CLASS = ['bg-primary text-primary-foreground', 'bg-muted-foreground/20 text-foreground', 'bg-muted-foreground/10 text-muted-foreground'];
const rankClass = (idx) => RANK_CLASS[idx] || 'text-muted-foreground';
const totalCellClass = (isTotal, val) => {
  if (!isTotal) return 'text-foreground';
  if (val > 0) return 'text-green-600 font-black';
  if (val < 0) return 'text-red-500 font-black';
  return 'text-muted-foreground';
};
const sortArrow = (active, dir) => {
  if (!active) return '';
  return dir === 'desc' ? ' ↓' : ' ↑';
};

export const Leaderboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('total');
  const [sortDir, setSortDir] = useState('desc');

  const fetchLeaderboard = useCallback(async (gameType) => {
    setLoading(true);
    try {
      const params = gameType && gameType !== 'all' ? `?game_type=${gameType}` : '';
      const res = await axios.get(`${API}/betting/leaderboard${params}`);
      setData(res.data);
    } catch (err) { console.error('Leaderboard fetch failed:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLeaderboard(filter); }, [fetchLeaderboard, filter]);

  const filters = [
    { value: 'all', label: 'All' },
    { value: 'nassau', label: 'Nassau' },
    { value: 'skins', label: 'Skins' },
    { value: 'best_ball', label: 'Best Ball' },
    { value: 'wolf', label: 'Wolf' },
    { value: 'match_play', label: 'Match' },
  ];

  const columns = [
    { key: 'name', label: 'Player', align: 'left' },
    { key: 'handicap', label: 'Hcp', fmt: (v, row) => {
      if (v == null) return '-';
      const src = row?.handicap_source;
      const mark = HCP_MARK[src] || '';
      return <span className="whitespace-nowrap">{v}<sup className="text-[7px] text-muted-foreground ml-px">{mark}</sup></span>;
    }},
    { key: 'total', label: 'P&L', fmt: formatMoney },
    { key: 'rounds_won', label: 'Wins' },
    { key: 'rounds_played', label: 'Rnds' },
    { key: 'holes_won', label: 'Holes W' },
    { key: 'birdies', label: 'Birdies' },
    { key: 'pars', label: 'Pars' },
    { key: 'eagles', label: 'Eagles' },
    { key: 'avg_score', label: 'Avg', fmt: v => v != null ? v : '-' },
    { key: 'best_round', label: 'Best', fmt: v => v != null ? v : '-' },
  ];

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sorted = data?.leaderboard ? [...data.leaderboard].sort((a, b) => {
    let av = a[sortBy], bv = b[sortBy];
    if (av == null) av = sortBy === 'name' ? '' : -Infinity;
    if (bv == null) bv = sortBy === 'name' ? '' : -Infinity;
    if (sortBy === 'name') {
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    }
    return sortDir === 'desc' ? bv - av : av - bv;
  }) : [];

  return (
    <div data-testid="leaderboard-section">
      <div className="flex gap-1.5 overflow-x-auto pb-3 mb-4 scrollbar-none">
        {filters.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filter === f.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
            data-testid={`filter-${f.value}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-xl animate-pulse bg-muted" />)}</div>
      ) : !sorted.length ? (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-muted" />
          <h3 className="font-serif text-lg font-semibold mb-2 text-foreground">No Results Yet</h3>
          <p className="text-sm text-muted-foreground">Complete some games to see the leaderboard</p>
        </div>
      ) : (
        <div>
          <p className="text-xs text-muted-foreground mb-3">{data.total_rounds} completed rounds</p>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-xs" data-testid="leaderboard-table">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-1.5 py-2 text-center text-[10px] font-bold uppercase text-muted-foreground w-7">#</th>
                  {columns.map(col => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className={`px-1.5 py-2 ${col.align === 'left' ? 'text-left' : 'text-center'} text-[10px] font-bold uppercase cursor-pointer select-none transition-colors hover:text-primary ${sortBy === col.key ? 'text-primary' : 'text-muted-foreground'}`}
                      data-testid={`sort-${col.key}`}
                    >
                      {col.label}{sortArrow(sortBy === col.key, sortDir)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((p, idx) => (
                  <tr
                    key={p.name}
                    className={`border-b border-border last:border-0 transition-colors ${idx === 0 ? 'bg-primary/5' : 'bg-card hover:bg-muted/30'}`}
                    data-testid={`leaderboard-row-${idx}`}
                  >
                    <td className="px-1.5 py-2 text-center">
                      <span className={`inline-flex w-5 h-5 rounded-full items-center justify-center text-[10px] font-black ${rankClass(idx)}`}>
                        {idx + 1}
                      </span>
                    </td>
                    {columns.map(col => {
                      const val = p[col.key];
                      const display = col.fmt ? col.fmt(val, p) : val;
                      const isTotal = col.key === 'total';
                      return (
                        <td
                          key={col.key}
                          className={`px-1.5 py-2 font-semibold ${col.align === 'left' ? 'text-left' : 'text-center'} ${col.key === 'name' ? 'font-bold text-foreground max-w-[80px] truncate' : ''} ${totalCellClass(isTotal, val)}`}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-4 mt-2 px-1 text-[10px] text-muted-foreground" data-testid="hcp-legend">
            <span>* Estimated from rounds</span>
            <span>** Manually entered</span>
            <span>No mark = Official GHIN</span>
          </div>
        </div>
      )}
    </div>
  );
};
